'use client';
import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { ArrowRight, MapPin, TrendingUp, Star } from 'lucide-react';

const cities = [
{
  id: 'city-blr',
  name: 'Bengaluru',
  count: '15,200+',
  label: 'Properties',
  href: '/listings-page?city=bengaluru',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18dee1303-1782047341303.png",
  imageAlt: 'Bengaluru city skyline with modern IT corridor buildings',
  trending: true
},
{
  id: 'city-mys',
  name: 'Mysuru',
  count: '3,400+',
  label: 'Properties',
  href: '/listings-page?city=mysuru',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e36b899a-1782047340965.png",
  imageAlt: 'Mysuru city with heritage architecture and residential areas',
  trending: false
},
{
  id: 'city-hub',
  name: 'Hubli-Dharwad',
  count: '2,100+',
  label: 'Properties',
  href: '/listings-page?city=hubli',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e029b73d-1782047342035.png",
  imageAlt: 'Residential neighbourhood in Hubli with modern apartments',
  trending: false
},
{
  id: 'city-mng',
  name: 'Mangaluru',
  count: '1,800+',
  label: 'Properties',
  href: '/listings-page?city=mangaluru',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1920635e4-1782047340780.png",
  imageAlt: 'Coastal city Mangaluru with sea-facing residential properties',
  trending: false
}];


export default function MapHomepage() {
  return (
    <div className="overflow-x-hidden">
      {/* Cities section */}
      <section className="section-bg-white py-20">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="section-label mb-2.5">Explore by City</p>
              <h2 className="section-heading">Top Cities Across<br />Karnataka</h2>
              <p className="section-subheading mt-3" style={{ maxWidth: '440px' }}>
                Find properties in Karnataka&apos;s most sought-after cities — from tech hubs to heritage towns.
              </p>
            </div>
            <Link
              href="/listings-page"
              className="hidden md:flex items-center gap-2 text-[13px] font-600 text-[#D93025] hover:text-[#B71C1C] transition-colors group">
              
              Explore All Cities
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {cities?.map((city) =>
            <Link
              key={city?.id}
              href={city?.href}
              className="category-card block group"
              style={{ height: '280px' }}>
              
                <div className="relative w-full h-full">
                  <AppImage
                  src={city?.image}
                  alt={city?.imageAlt}
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                
                  <div className="absolute inset-0" style={{
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.22) 40%, rgba(0,0,0,0.78) 100%)'
                }} />

                  {city?.trending &&
                <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center gap-1 bg-[#D93025] text-white text-[10px] font-700 px-2.5 py-1 rounded-full" style={{ letterSpacing: '0.04em' }}>
                        <TrendingUp size={9} />
                        Trending
                      </span>
                    </div>
                }

                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex items-center gap-1 mb-1.5">
                      <MapPin size={10} className="text-white/60" />
                      <p className="text-white/60 text-[11px] font-500">{city?.count} {city?.label}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <h3 className="text-white font-700" style={{ fontSize: '20px', letterSpacing: '-0.02em' }}>
                        {city?.name}
                      </h3>
                      <div className="w-8 h-8 rounded-full bg-white/12 border border-white/20 flex items-center justify-center group-hover:bg-[#D93025] group-hover:border-[#D93025] transition-all duration-300">
                        <ArrowRight size={13} className="text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </div>
      </section>
      {/* Why NammaPrice section */}
      <section className="section-bg-light py-20">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="section-label mb-2.5">Why Choose Us</p>
            <h2 className="section-heading">The NammaPrice Advantage</h2>
            <p className="section-subheading mt-3 mx-auto" style={{ maxWidth: '480px' }}>
              Built for Karnataka — with local expertise, verified listings, and a platform you can trust.
            </p>
          </div>

          {/* Asymmetric feature layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* Large feature */}
            <div className="lg:col-span-5 bg-[#D93025] rounded-[20px] p-8 flex flex-col justify-between" style={{ minHeight: '280px' }}>
              <div className="w-12 h-12 rounded-[14px] bg-white/15 flex items-center justify-center mb-6">
                <Star size={22} className="text-white" fill="currentColor" />
              </div>
              <div>
                <h3 className="text-white font-700 mb-3" style={{ fontSize: '22px', letterSpacing: '-0.02em' }}>
                  Verified Listings Only
                </h3>
                <p className="text-white/70 text-[14px] leading-relaxed">
                  Every listing is manually reviewed by our team. No fake listings, no spam — just genuine properties from verified sellers.
                </p>
              </div>
            </div>

            {/* 2 medium features */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
              {
                icon: '🗺️',
                title: 'Map-First Search',
                desc: 'Find properties by location with our interactive map. See price markers, filter by area, and explore neighbourhoods.'
              },
              {
                icon: '⚡',
                title: 'Instant Alerts',
                desc: 'Get notified the moment a new property matching your criteria is listed. Never miss a deal.'
              },
              {
                icon: '🏦',
                title: 'Home Loan Assistance',
                desc: 'Connect with top banks — SBI, HDFC, ICICI — directly from the listing page. Get pre-approved in minutes.'
              },
              {
                icon: '📋',
                title: 'Karnataka Legal Docs',
                desc: 'EC, Khata, BMRDA, RERA — all legal document statuses are shown upfront. No surprises.'
              }]?.
              map((feat, i) =>
              <div key={`feat-${i}`} className="bg-white rounded-[16px] p-6 border border-[#E2E2E7] hover:border-[#D93025]/30 hover:shadow-card transition-all duration-200">
                  <div className="text-2xl mb-4">{feat?.icon}</div>
                  <h3 className="font-700 text-[#1D1D1F] mb-2" style={{ fontSize: '15px', letterSpacing: '-0.01em' }}>
                    {feat?.title}
                  </h3>
                  <p className="text-[#6E6E73] text-[13px] leading-relaxed">{feat?.desc}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Banner */}
      <section className="section-bg-dark py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, #D93025 0%, transparent 50%), radial-gradient(circle at 80% 50%, #2563EB 0%, transparent 50%)'
        }} />
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <p className="text-[#D93025] text-[12px] font-600 uppercase tracking-widest mb-3">Post Your Listing</p>
              <h2 className="text-white font-700 mb-3" style={{ fontSize: 'clamp(28px, 4vw, 44px)', letterSpacing: '-0.03em', lineHeight: 1.15 }}>
                Sell or Rent Your Property<br />
                <span className="text-white/50 font-300" style={{ fontStyle: 'italic', fontFamily: 'DM Serif Display, serif' }}>for free, today</span>
              </h2>
              <p className="text-white/50 text-[15px]" style={{ maxWidth: '440px' }}>
                Join 52,000+ listings on NammaPrice. Post your property in under 5 minutes — no hidden charges.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Link
                href="/post-listing"
                className="btn-primary px-8"
                style={{ height: '52px', fontSize: '15px', borderRadius: '12px' }}>
                
                Post Free Ad
                <ArrowRight size={16} />
              </Link>
              <Link
                href="/listings-page"
                className="flex items-center justify-center gap-2 px-8 py-3 border border-white/20 text-white/80 rounded-[12px] text-[15px] font-600 hover:bg-white/8 hover:text-white transition-all">
                
                Browse Listings
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>);

}